import React, { ReactNode, useCallback, useContext, useState } from 'react';
import { ModalContainer } from '../../components/modals/ModalContainer';

interface ModalContextProps {
  modal: ReactNode | undefined;
  setModal: (content: ReactNode | null, props?: ModalProps) => void;
  closeModal: (props?: ModalProps) => void;
}

interface ModalProps {
  onClose?: () => void;
  ignoreOnClose?: boolean;
  [key: string]: any;
}

interface QueueItem {
  content: ReactNode;
  props: ModalProps;
}

const ModalContext = React.createContext<ModalContextProps | undefined>(
  undefined
);
const { Provider } = ModalContext;

const useModal = (): ModalContextProps => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

interface ModalProviderProps {
  children: ReactNode;
}

const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modalQueue, setModalQueue] = useState<QueueItem[]>([]);

  const [{ content: modal, props: modalProps } = {} as QueueItem] = modalQueue;

  const closeModal = useCallback(
    (props: ModalProps = modalProps) => {
      if (props?.onClose && !props?.ignoreOnClose) {
        props.onClose();
      }
      setModalQueue((prevQueue) => prevQueue.slice(1));
    },
    [modalProps]
  );

  const setModal = useCallback(
    (content: ReactNode | null, props: ModalProps = {}) => {
      if (!content) {
        return closeModal();
      }

      setModalQueue((prevQueue) => {
        const isModalInQueue = prevQueue.some(
          ({ content: queuedContent }) =>
            (queuedContent as any)?.type?.name === (content as any)?.type?.name
        );
        if (isModalInQueue) {
          return prevQueue;
        }
        return [...prevQueue, { content, props }];
      });
    },
    [closeModal]
  );

  return (
    <Provider value={{ modal, setModal, closeModal }}>
      <ModalContainer
        modal={modal}
        modalProps={modalProps}
        closeModal={closeModal}
      />
      {children}
    </Provider>
  );
};

export { ModalContext, ModalContainer, ModalProvider, useModal };
export type { ModalProps };
